import qrcode from 'qrcode-generator';
import { t } from '../core/i18n.js';
import { tvnav } from '../core/tvnav.js';
import { notify } from '../core/notify.js';
import { escapeHtml } from '../core/utils.js';
import { startRelay, formFields, setFieldValue, submitField } from '../core/relay-type.js';

/* «Escribir con el móvil» en la tele: QR + código mientras el móvil rellena el formulario entero (todos los campos de texto de la
   ventana o la página donde está el campo enfocado). Ver core/relay-type.js. */

function qrSvg(text) {
    try {
        const qr = qrcode(0, 'M');
        qr.addData(text);
        qr.make();
        return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
    } catch (e) {
        return '';
    }
}

const masked = (txt, secret) => (secret ? '•'.repeat(Math.min(String(txt).length, 24)) : escapeHtml(String(txt).length > 60 ? String(txt).slice(0, 60) + '…' : String(txt)));

/** Abre la ventana para `input` (y los demás campos de su formulario). Si no hay conexión con el servidor, avisa y deja el teclado de la tele. */
export function openRelayTyping(input) {
    const app = window.app;
    const id = 'relay-' + Date.now().toString(36);
    const form = formFields(input);
    const fields = form.fields;
    let session = null;
    let closing = false;
    let submitted = false;
    const popup = app.popup.create({
        content: `<div class="popup catpick-popup admin-popup phone-popup relay-popup" id="${id}">
            <div class="page">
                <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">
                    <div class="title">${escapeHtml(t('relay.title'))}</div>
                    <div class="right"><a href="#" class="link popup-close btn-icon" aria-label="${escapeHtml(t('common.close'))}"><span class="mi">close</span></a></div>
                </div></div>
                <div class="page-content">
                    <div class="phone-body" data-relay-body><div class="spinner"></div></div>
                </div>
            </div>
        </div>`,
        on: {
            open(p) {
                const el = p.el || document.getElementById(id);
                const body = el.querySelector('[data-relay-body]');
                const fail = () => {
                    if (closing) return;
                    notify.error(t('relay.error'));
                    popup.close();
                };
                // Lo que lleva cada campo, tal como lo va escribiendo el móvil (las contraseñas, con puntos).
                const fieldsHtml = () => `<ul class="relay-fields">${fields.map((f, i) => `<li class="${i === form.current ? 'is-current' : ''}"><span class="relay-field-name">${escapeHtml(f.info.label || t('relay.fieldN', { n: i + 1 }))}</span><span class="relay-field-value">${f.el.value ? masked(f.el.value, f.info.kind === 'password') : '<i>—</i>'}</span></li>`).join('')}</ul>`;
                const paint = (state) => {
                    body.innerHTML = `
                        <p class="pw-note">${escapeHtml(t('relay.step1', { url: session.host }))}</p>
                        <div class="phone-grid">
                            <div class="pair-qr phone-qr" aria-hidden="true">${qrSvg(session.url)}</div>
                            <div class="phone-side">
                                <p class="pw-note">${escapeHtml(fields.length > 1 ? t('relay.fields', { n: fields.length }) : (fields[0].info.label ? t('relay.field', { name: fields[0].info.label }) : ''))}</p>
                                <div class="phone-url">${escapeHtml(session.host)}</div>
                                <div class="phone-pin-label">${escapeHtml(t('relay.code'))}</div>
                                <div class="pair-code phone-pin">${session.code.split('').join(' ')}</div>
                            </div>
                        </div>
                        <div class="phone-status" aria-live="polite">${escapeHtml(state)}</div>
                        ${fieldsHtml()}
                        <p class="pw-note relay-privacy"><span class="mi">lock</span> ${escapeHtml(t('relay.privacy'))}</p>
                        <button type="button" class="btn btn-primary btn-lg btn-block" data-relay-done data-nav-default><span class="mi">check</span>${escapeHtml(t('relay.done'))}</button>`;
                    body.querySelector('[data-relay-done]').addEventListener('click', () => popup.close());
                    if (!paint.focused) { paint.focused = true; setTimeout(() => tvnav.focusLayer(el), 60); }
                };
                // Mientras se escribe solo se pone al día la lista (sin repintar todo: el foco del mando se quedaría donde está).
                const refreshFields = () => { const ul = body.querySelector('.relay-fields'); if (ul) ul.outerHTML = fieldsHtml(); };
                startRelay({
                    fields: fields.map((f) => f.info),
                    current: form.current,
                    on: {
                        join: () => paint(t('relay.connected')),
                        text: (txt, i) => { const f = fields[i] || fields[0]; if (f && document.contains(f.el)) setFieldValue(f.el, txt); refreshFields(); },
                        submit: () => { closing = true; submitted = true; popup.close(); setTimeout(() => submitField(fields[fields.length - 1].el), 200); },
                        closed: () => { if (!closing) paint(t('relay.ended')); },
                        error: fail
                    }
                }).then((s) => {
                    session = s;
                    popup._relay = s;
                    if (closing) { s.stop(); return; }
                    paint(t('relay.waiting'));
                }).catch(fail);
            },
            closed() {
                closing = true;
                if (popup._relay) popup._relay.stop();
                try { popup.destroy(); } catch (e) { /* ya destruido */ }
                const el = document.getElementById(id);
                if (el && el.parentNode) el.parentNode.removeChild(el);
                // Vuelve el foco al campo para seguir con el mando (si se envió, el formulario ya hizo lo suyo).
                if (!submitted) { try { if (document.contains(input)) tvnav.focus(input); } catch (e) { /* nada */ } }
            }
        }
    });
    popup.open();
    return popup;
}
